import { useSelector } from "react-redux";

/**
 * The Date & Time setting (Settings -> Date and Time) is stored as a MOMENT
 * format string, e.g. "DD MMMM, YYYY" or "MM/DD/YYYY". react-datepicker formats
 * through date-fns instead, where day-of-month and year tokens are lowercase
 * (`D` is day-of-year and `YYYY` is week-numbering year), so the raw value
 * cannot be passed to `<DatePicker dateFormat={...} />`.
 *
 * Mirrors apps/frontend/src/utils/dateFormat.ts so both apps show and parse
 * dates in the same configured format.
 */

// Month tokens are identical in both libraries. Longest-first, because the
// regex alternation below is first-match and `YY` must not win over `YYYY`.
const MOMENT_TO_DATE_FNS = {
  YYYY: "yyyy",
  YY: "yy",
  DD: "dd",
  D: "d",
};

/** Used until general settings have loaded. */
export const DEFAULT_DATE_PICKER_FORMAT = "MM/dd/yyyy";

/**
 * @description Converts the admin-configured moment format into the equivalent
 * date-fns format for react-datepicker.
 * @param {String} momentFormat e.g. `DD MMMM, YYYY`
 * @example toDatePickerFormat("DD/MM/YYYY") // "dd/MM/yyyy"
 */
export const toDatePickerFormat = (momentFormat) => {
  if (!momentFormat) return DEFAULT_DATE_PICKER_FORMAT;
  return momentFormat.replace(
    /YYYY|YY|DD|D/g,
    (token) => MOMENT_TO_DATE_FNS[token]
  );
};

/** The same value, read from the general settings in the store. */
export const useDatePickerFormat = () => {
  const dateFormat = useSelector(
    (state) => state.settings?.generalSettings?.dateFormat
  );
  return toDatePickerFormat(dateFormat);
};
